<template>
    <div class="unit-search">
        <form @submit.prevent="submit" method="PUT" accept-charset="UTF-8" novalidate="novalidate">
            <div class="row">
                <div class="col-sm-6 form-group has-feedback" :class="{'has-error': errors.has('first_name')}">
                    <label for="user-first-name">First Name</label>

                    <input id="user-first-name" v-model="user.first_name" name="first_name" type="text" class="form-control" />

                    <span v-show="errors.has('first_name')" class="help-block">
                        {{ errors.first("first_name") }}
                    </span>
                </div>

                <div class="col-sm-6 form-group has-feedback" :class="{'has-error': errors.has('last_name')}">
                    <label for="user-last-name">Last Name</label>

                    <input id="user-last-name" v-model="user.last_name" name="last_name" type="text" class="form-control" />

                    <span v-show="errors.has('last_name')" class="help-block">
                        {{ errors.first("last_name") }}
                    </span>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-7 form-group has-feedback" :class="{'has-error': errors.has('email')}">
                    <label for="user-email">Email</label>

                    <input id="user-email" v-model="user.email" name="email" type="email" class="form-control" />

                    <span v-show="errors.has('email')" class="help-block">
                        {{ errors.first("email") }}
                    </span>
                </div>

                <div class="col-sm-5 form-group has-feedback" :class="{'has-error': errors.has('phone')}">
                    <label for="user-phone">Phone</label>

                    <input id="user-phone" v-model="user.phone" name="phone" type="text" class="form-control" />

                    <span v-show="errors.has('phone')" class="help-block">
                        {{ errors.first("phone") }}
                    </span>
                </div>
            </div>

            <div class="row">
                <div class="col-sm-8 form-group has-feedback" :class="{'has-error': errors.has('unit_id') && !this.showUnitInput}">
                    <auto-complete
                        class="form-group"
                        :blurred="this.showUnitInput"
                        name="unit_id"
                        label="Address Lookup"
                        v-if="project.site_manager_integrated && project.use_tami_rent_roll && project.tami_rent_roll_initial_sync_ran && project.enable_network_credentials && project.credential_portal === 1"
                        tooltip-text="This field is required to grant network access"
                        search-results-index="Units"
                        :query-string="'project_id=' + this.project.id"
                        find-result-index="Unit"
                        :resource-url="this.resourceUrl"
                        :theme-color="themeColor"
                        :initial-id="this.user.unit_id"
                        @change="setUnit"
                        @keyup="addSearchCount"
                    >
                        @verbatim
                        <template slot-scope="unit" slot="model-display">
                            {{ unit.full_address }}
                        </template>
                        @endverbatim
                    </auto-complete>
                    <auto-complete v-else class="form-group" :blurred="this.showUnitInput" name="unit_id" label="Address Lookup" search-results-index="Units" :theme-color="themeColor" :query-string="'project_id=' + this.project.id" find-result-index="Unit" :resource-url="this.resourceUrl" :initial-id="this.user.unit_id" @change="setUnit" @keyup="addSearchCount">
                        @verbatim
                        <template slot-scope="unit" slot="model-display">
                            {{ unit.full_address }}
                        </template>
                        @endverbatim
                    </auto-complete>

                    <input type="hidden" name="unit_id" v-model="user.unit_id" />

                    <span v-show="errors.has('unit_id') && !this.showUnitInput" class="help-block">
                        {{ errors.first("unit_id") }}
                    </span>
                </div>
            </div>

            <div class="row">
                <h4 class="help-text-sm col-sm-4" style="margin-top: -20px" v-if="this.searchCount >= 2" v-on:click="unitNotFound">Don't see your unit? <span style="font-size: 12px" v-if="this.showUnitInput">&#9650;</span><span style="font-size: 12px" v-if="!this.showUnitInput">&#9660;</span></h4>
            </div>

            <div class="row">
                <div class="col-sm-8 form-group has-feedback" :class="{'has-error': errors.has('unit_search') && this.showUnitInput}" v-if="this.showUnitInput">
                    <label for="user-unit-search">Please Enter Your Full Address</label>

                    <input id="user-unit-search" v-model="user.unit_search" name="unit_search" type="text" class="form-control" />

                    <span v-show="errors.has('unit_search') && this.showUnitInput" class="help-block">
                        {{ errors.first("unit_search") }}
                    </span>
                </div>
            </div>

            <p v-if="this.showUnitInput"><b>Warning:</b> Make sure to double check for your unit listed above to<br />ensure your enrollment in the {{ this.project.brand_suffix }}.</p>

            <hr />

            <div class="col-sm-6 form-group pull-right">
                <button v-if="themeColor" type="submit" class="btn btn-success btn-block" :disabled="loading" :style="'background-color: ' + themeColor">Submit</button>
                <button v-else type="submit" class="btn btn-success btn-block" :disabled="loading">Submit</button>
            </div>
        </form>
    </div>
</template>

<script>
import AutoComplete from "./AutoComplete";

export default {
    components: {AutoComplete},

    props: {
        user: {
            required: true,
            type: Object,
        },

        themeColor: {
            required: false,
            type: String,
        },

        project: {
            required: true,
            type: Object,
        },

        resourceUrl: {
            required: true,
            type: String,
        },

        appUrl: {
            required: true,
            type: String,
        },
    },

    data() {
        return {
            unitNotListed: false,
            searchCount: 0,
            showUnitInput: false,
            unit: {
                unit_id: null,
                full_address: null,
            },
            loading: false,
        };
    },

    methods: {
        addSearchCount(event) {
            if (this.searchCount <= 1) {
                this.searchCount += 1;
            }
        },

        unitNotFound() {
            this.showUnitInput = !this.showUnitInput;
        },

        setUnit(model_id, model) {
            if (model_id && !this.showUnitInput) {
                this.user.unit_id = model_id;
            } else {
                this.user.unit_id = null;
            }
        },

        submit() {
            this.loading = true;
            this.errors.clear();
            if (this.showUnitInput) {
                this.user.unit_id = null;
            } else {
                this.user.unit_search = null;
            }

            return this.$validator.validateAll().then((valid) => {
                if (valid) {
                    return axios
                        .put("/users/" + this.user.id + "/unit", {
                            User: this.user,
                        })
                        .then((response) => {
                            if (response.data.errors) {
                                for (const [key, value] of Object.entries(response.data.errors)) {
                                    this.errors.add({
                                        field: key,
                                        msg: value[0],
                                    });
                                }
                            } else if (response.data.error) {
                                this.$notify({
                                    type: "danger",
                                    placement: "top-left",
                                    title: response.data.error,
                                });
                            } else {
                                window.location.href = this.appUrl + "/agreement";
                            }
                        })
                        .catch(this.$root.handleErrors)
                        .finally(() => {
                            this.loading = false;
                        });
                } else {
                    this.loading = false;
                }
            });
        },
    },
};
</script>
